import React, { useEffect, useRef, useState } from 'react';
import type { UnterweisungAbschnitt } from '../../../services/sifa/types';
import { MINDEST_LESEZEIT_MS, absaetze, leseFreigabe, videoSprungKorrektur } from './teilnahmeSeiteLogic';
import { HINWEISKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER } from './teilnahmeSeiteStil';

/**
 * Ein Abschnitt der Unterweisung — Text, Bild und/oder Video (Vorbild: ein
 * Abschnitt der Tablet-App = ein Video).
 *
 * MIT Video wird „Weiter" erst nach `ended` frei; Vorspulen wird auf die
 * hoechste gesehene Zeit zurueckgesetzt (gesehenMax-Ref + `onSeeking`,
 * Regel videoSprungKorrektur) — die Pflicht, es ganz zu sehen, laesst sich
 * nicht per Schieberegler abkuerzen. Zurueckspulen bleibt erlaubt.
 * OHNE Video gilt die Lese-Regel: bis zum Ende gescrollt oder
 * Mindestlesezeit um (leseFreigabe).
 *
 * Der Aufrufer setzt `key={abschnitt.id}`, damit Zeit, Scroll-Stand und die
 * gesehene Videozeit je Abschnitt neu beginnen. Ein Bild, das nicht laedt
 * (alter Pfad aus dem privaten Bucket), wird ausgeblendet statt als kaputtes
 * Symbol zu stehen.
 */
export const AbschnittAnsicht: React.FC<{
  abschnitt: UnterweisungAbschnitt;
  nummer: number;
  gesamt: number;
  onWeiter: () => void;
  onZurueck: () => void;
}> = ({ abschnitt, nummer, gesamt, onWeiter, onZurueck }) => {
  const [frei, setFrei] = useState(false);
  const [restSekunden, setRestSekunden] = useState(Math.ceil(MINDEST_LESEZEIT_MS / 1000));
  const [bildKaputt, setBildKaputt] = useState(false);
  const [videoKaputt, setVideoKaputt] = useState(false);
  /** Ein Video, das laedt, entscheidet allein; eines, das nicht laedt, faellt auf die Lese-Regel zurueck. */
  const mitVideo = !!abschnitt.videoUrl && !videoKaputt;
  // Startzeitpunkt erst im Effekt setzen — im Render waere Date.now() unrein.
  const start = useRef(0);
  /** Hoechste Videozeit in Sekunden, die wirklich abgespielt wurde. */
  const gesehenMax = useRef(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Autostart nach einer Sekunde: der Teilnehmer hat gerade „Weiter" gedrueckt,
  // die kurze Pause laesst die Seite erst stehen, bevor Ton einsetzt. Browser
  // duerfen `play()` ohne frische Geste verweigern (vor allem iOS) — dann
  // bleibt das Video mit Bedienelementen stehen und der Hinweis darunter sagt,
  // dass man auf Play tippen soll. Kein Abbruch, kein Fehler.
  const [autostartVerweigert, setAutostartVerweigert] = useState(false);
  useEffect(() => {
    if (!mitVideo) return undefined;
    const timer = setTimeout(() => {
      const v = videoRef.current;
      if (!v || v.ended) return;
      const versuch = v.play();
      if (versuch && typeof versuch.catch === 'function') {
        versuch.catch(() => setAutostartVerweigert(true));
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [mitVideo]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    // Mit Video entscheidet allein `ended` — keine Lesezeit, kein Scroll-Ende.
    if (mitVideo) return undefined;
    start.current = Date.now();
    const pruefen = () => {
      const doc = document.documentElement;
      const ueberlauf = doc.scrollHeight > window.innerHeight + 4;
      const scrollEnde = window.innerHeight + window.scrollY >= doc.scrollHeight - 8;
      const verstricheneMs = Date.now() - start.current;
      setRestSekunden(Math.max(0, Math.ceil((MINDEST_LESEZEIT_MS - verstricheneMs) / 1000)));
      if (leseFreigabe({ ueberlauf, scrollEnde, verstricheneMs })) setFrei(true);
    };
    const takt = setInterval(pruefen, 500);
    window.addEventListener('scroll', pruefen, { passive: true });
    window.addEventListener('resize', pruefen);
    pruefen();
    return () => {
      clearInterval(takt);
      window.removeEventListener('scroll', pruefen);
      window.removeEventListener('resize', pruefen);
    };
  }, [mitVideo]);

  const zeitGesehen = () => {
    const v = videoRef.current;
    if (!v) return;
    // Nur echtes Abspielen hebt die Marke — ein Sprung nach vorn wird in
    // onSeeking zurueckgesetzt, bevor timeupdate ihn festschreiben koennte.
    if (!v.seeking && v.currentTime > gesehenMax.current) gesehenMax.current = v.currentTime;
  };

  const sprungPruefen = () => {
    const v = videoRef.current;
    if (!v) return;
    const zurueckAuf = videoSprungKorrektur(v.currentTime, gesehenMax.current);
    if (zurueckAuf !== null) v.currentTime = zurueckAuf;
  };

  /**
   * Zu Ende: „Weiter" frei — und das Vollbild wieder verlassen. Auf dem
   * Handy laeuft das Video meist im Vollbild; bliebe es dort, saehe der
   * Teilnehmer den frei gewordenen Knopf nicht und hielte die Seite fuer
   * haengend. Desktop-Browser haben `document.exitFullscreen`, der
   * iPhone-Player nur das eigene `webkitExitFullscreen` auf dem Element.
   */
  const videoZuEnde = () => {
    const v = videoRef.current;
    if (v && Number.isFinite(v.duration)) gesehenMax.current = v.duration;
    setFrei(true);
    try {
      if (document.fullscreenElement && document.exitFullscreen) void document.exitFullscreen();
      const ios = v as (HTMLVideoElement & { webkitDisplayingFullscreen?: boolean; webkitExitFullscreen?: () => void }) | null;
      if (ios?.webkitDisplayingFullscreen && ios.webkitExitFullscreen) ios.webkitExitFullscreen();
    } catch {
      // Vollbild verlassen ist Komfort; scheitert es, bleibt der Knopf trotzdem frei.
    }
  };

  /**
   * Keine Wiedergabegeschwindigkeit: `controlsList="noplaybackrate"` nimmt
   * die Auswahl aus den Chromium-Bedienelementen; Browser, die das Attribut
   * nicht kennen, setzen wir hier auf 1,0 zurueck, sobald jemand dreht.
   */
  const tempoZuruecksetzen = () => {
    const v = videoRef.current;
    if (v && v.playbackRate !== 1) v.playbackRate = 1;
  };

  // Laedt das Video nicht (Pfad ins Leere, Format unbekannt), darf der
  // Teilnehmer nicht festhaengen: `mitVideo` kippt, der Effekt startet die
  // Lese-Regel ueber den Text.
  const videoFehlt = () => setVideoKaputt(true);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <p className="text-sm font-medium text-slate-500">
          Abschnitt {nummer} von {gesamt}
        </p>
        <h2 className="text-xl font-bold text-slate-900">{abschnitt.titel}</h2>
      </div>

      {mitVideo && (
        <video
          ref={videoRef}
          src={abschnitt.videoUrl ?? undefined}
          controls
          playsInline
          preload="metadata"
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          className="w-full rounded-xl border border-slate-200 bg-black"
          onTimeUpdate={zeitGesehen}
          onSeeking={sprungPruefen}
          onRateChange={tempoZuruecksetzen}
          onEnded={videoZuEnde}
          onError={videoFehlt}
        >
          Dein Browser kann dieses Video nicht abspielen.
        </video>
      )}

      {abschnitt.bildUrl && !bildKaputt && (
        <img
          src={abschnitt.bildUrl}
          alt={abschnitt.titel}
          className="w-full rounded-xl border border-slate-200"
          onError={() => setBildKaputt(true)}
        />
      )}

      <div className="space-y-4 leading-relaxed text-slate-800">
        {absaetze(abschnitt.text).map((a, i) => (
          <p key={i} className="whitespace-pre-line">
            {a}
          </p>
        ))}
      </div>

      {!frei && (
        <div className={HINWEISKASTEN} aria-live="polite">
          {mitVideo
            ? `${autostartVerweigert ? 'Bitte auf Play tippen. ' : ''}Bitte das Video bis zum Ende ansehen — „Weiter" wird danach frei. Vorspulen ist nicht möglich.`
            : `Bitte bis zum Ende lesen${restSekunden > 0 ? ` — „Weiter" ist in ${restSekunden} s frei.` : '.'}`}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <button type="button" onClick={onZurueck} className={KNOPF_SEKUNDAER}>
          Zurück
        </button>
        <button type="button" onClick={onWeiter} disabled={!frei} className={KNOPF_PRIMAER}>
          Weiter
        </button>
      </div>
    </div>
  );
};
